import { FC, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { Typography } from '@beeline/design-system-react/extracted-styles';

import { DeskAttributes as BaseDeskAttributes } from 'src/components/DeskAttributes/DeskAttributes';
import { Section } from 'src/components/Section/Section';
import { deskRatio } from 'src/pages/AdministratorPage/consts';
import { getAdminState } from 'src/store/admin/selectors';
import { adminActions } from 'src/store/admin/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { Desk } from 'src/types';

import { CoordsFields } from './CoordsFields/CoordsFields';
import classes from './Fields.module.scss';
import { FontSizeField } from './FontSizeField';
import { ResponsibleField } from './ResponsibleField';
import { RotationField } from './RotationField/RotationField';
import { TitleField } from './TitleField';
import { WidthField } from './WidthField';

interface DeskAttributesProps {
  bookingObject: Desk;
}

const DeskAttributes: FC<DeskAttributesProps> = ({ bookingObject }) => {
  const dispatch = useAppDispatch();
  const { placeLoading } = useSelector(getAdminState);

  const changeByWindow = useCallback(
    (checked: boolean) => {
      dispatch(adminActions.update({ byWindow: checked }));
    },
    [dispatch],
  );

  const changeWithMonitor = useCallback(
    (checked: boolean) => {
      dispatch(
        adminActions.update({
          withMonitor: checked,
        }),
      );
    },
    [dispatch],
  );

  const changeWithDivider = useCallback(
    (checked: boolean) => {
      dispatch(
        adminActions.update({
          withDivider: checked,
        }),
      );
    },
    [dispatch],
  );

  return (
    <BaseDeskAttributes
      byWindow={!!bookingObject.byWindow}
      onChangeByWindow={changeByWindow}
      withMonitor={!!bookingObject.withMonitor}
      onChangeWithMonitor={changeWithMonitor}
      withDivider={!!bookingObject.withDivider}
      onChangeWithDivider={changeWithDivider}
      loading={placeLoading}
    />
  );
};

export interface DeskFieldsProps {
  bookingObject: Desk;
}

export const DeskFields: FC<DeskFieldsProps> = ({ bookingObject }) => {
  return (
    <>
      <Section title='Укажите данные ОБ' testId='administratorDeskData'>
        <TitleField label='Номер рабочего места' />
      </Section>

      <Section
        title='Координаты и размер'
        testId='administratorCoordsAndSize'
        contentClassName={classes.attributes}
      >
        <CoordsFields bookingObject={bookingObject} />
        <WidthField ratio={deskRatio} />
        <FontSizeField />
      </Section>

      <Section title='Вращение' testId='administratorRotation'>
        <RotationField
          refreshId={bookingObject.id}
          value={bookingObject.rotation}
          min={0}
          max={270}
          step={90}
        />
      </Section>

      <div className={classes.attributes}>
        <Typography
          variant='subtitle1'
          data-testid='administratorDeskAttributes'
        >
          Что в себя включает ОБ
        </Typography>
        <DeskAttributes bookingObject={bookingObject} />
      </div>

      <Section title='Закрепить за' testId='administratorResponsible'>
        <ResponsibleField
          id={bookingObject.mapPlaceId}
          value={bookingObject.responsible}
        />
      </Section>
    </>
  );
};
